import { useEffect } from 'react';
import { Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { mapFeedMessage, useApiQuery } from 'theSource/utils';
import { getFeedMessageQueryKey, getFormattedDate } from './utils';
import { FeedMessageResponseDto } from './types';
import {
  Button,
  InternalPage,
  Loader,
  PageNotFound,
  Spacer,
} from 'theSource/components';
import {
  ImageStyled,
  MessageContentStyled,
  TitleStyled,
} from './InboxMessagePageStyled';
import { decrementFeedMessageCount, setViewedIDs } from '../services';
import defaultInboxImage from 'theSource/assets/Inbox/defaultInboxImage.jpg';

export const InboxMessagePage = () => {
  const { t } = useTranslation('pages.inbox');
  const { messageId } = useParams();
  const dispatch = useDispatch();

  const { data: feedMessageData, isLoading: isLoadingFeedMessage } =
    useApiQuery<FeedMessageResponseDto>({
      queryKey: getFeedMessageQueryKey(messageId ?? ''),
      endpoint: `/feed_messages/${messageId}`,
      showErrorSnackbar: false, // BE returns `HTTP500: Unexpected error` if message was not found.
    });

  const feedMessage = feedMessageData && mapFeedMessage(feedMessageData.data);

  const handleLinkButtonClick = () => window.open(feedMessage?.link, '_blank');

  // we want to decrease the unread messages count by one each time we open an unread message
  useEffect(() => {
    if (feedMessage && !isLoadingFeedMessage && !feedMessage.read) {
      dispatch(decrementFeedMessageCount(feedMessage.id));
      dispatch(setViewedIDs(feedMessage.id));
    }
  });

  if (isLoadingFeedMessage) {
    return <Loader />;
  }

  if (!feedMessage) {
    return <PageNotFound />;
  }

  return (
    <InternalPage testId="inbox-message-page" withBackLink>
      <Typography variant="caption">
        {getFormattedDate(feedMessage.createdAt)}
      </Typography>
      <Spacer height={1} />
      <TitleStyled variant="h5">{feedMessage.title}</TitleStyled>
      <Spacer height={4} />
      <ImageStyled
        src={
          feedMessage.imageUrl !== '' ? feedMessage.imageUrl : defaultInboxImage
        }
        alt={t('messageImage')}
      />
      <Spacer height={4} />
      <MessageContentStyled
        data-testid="message-body"
        variant="body1"
        dangerouslySetInnerHTML={{ __html: feedMessage.body }}
      />
      <Spacer height={8} />
      <>
        {feedMessage?.link && feedMessage.linkLabel && (
          <Button
            data-testid="external-link-button"
            fullWidth
            label={feedMessage.linkLabel}
            variant="primary"
            type="button"
            onClick={handleLinkButtonClick}
          />
        )}
      </>
    </InternalPage>
  );
};
